import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonGroup from '@jetbrains/ring-ui/components/button-group/button-group'
import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import {memo, Suspense, useCallback, useContext, useEffect, useMemo} from 'react'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import {useSyncedBooleanQueryParam} from '../../../../../hooks/routes'
import {getBuildChangesArg, getChangesArg} from '../../../../../rest/arguments'
import {getBuild, getBuildType} from '../../../../../selectors'
import {restApi} from '../../../../../services/rest'
import type {BuildId, BuildTypeId} from '../../../../../types'
import {ChangesType} from '../../../../../types'
import {subscribeOnBuildTypeEvents} from '../../../../../utils/subscriber'
import {BUILD_CHANGES_LOADED} from '../../../../../utils/subscriptionEvents'
import {UserProperties} from '../../../../../utils/userProperties'
import ArtifactChanges from '../../../../common/ArtifactChanges/ArtifactChanges'
import {SequenceLoaderContext} from '../../../../common/SequenceLoader/SequenceLoader.hooks'
import type {SeqProps} from '../../../../common/SequenceLoader/SequenceLoader.types'
import type {CommiterSelectQuery} from '../../../../common/UserSelect/__generated__/CommiterSelectQuery.graphql'
import {
  ChangeContext,
  DEFAULT_CHANGES_LIST_CONTEXT,
} from '../../../../packages/Changes/Change/Change.context'
import {VISIBLE_COUNT} from '../../../../packages/Changes/Changes.consts'
import {useChangesCountHeading, useChangesFilter} from '../../../../packages/Changes/Changes.hooks'
import {
  getArtifactChangesCount,
  getBuildChangesCount,
} from '../../../../packages/Changes/Changes.selectors'
import ChangesFilter from '../../../../packages/Changes/ChangesFilter/ChangesFilter'
import ChangesLog from '../../../../packages/Changes/ChangesLog/ChangesLog'
import {useChangeLogLocator} from '../../../../packages/Changes/ChangesLog/ChangesLog.hooks'
import {getChangeLogArg} from '../../../../packages/Changes/ChangesLog/ChangesLog.utils'

import styles from './ChangesAdvancedView.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = SeqProps & {
  className?: string
  buildId: BuildId
  buildTypeId: BuildTypeId
  commiterSelectQueryRef: PreloadedQuery<CommiterSelectQuery>
  baseLocator: string
}

function ChangesAdvancedView({buildId, buildTypeId, commiterSelectQueryRef, baseLocator}: Props) {
  const {filterOptions, onChangeFilter} = useChangesFilter(buildTypeId)

  const queued = useAppSelector(state => getBuild(state, buildId)?.state === 'queued')
  const running = useAppSelector(state => getBuild(state, buildId)?.state === 'running')
  const buildTypeInternalId = useAppSelector(state => getBuildType(state, buildTypeId)?.internalId)

  const [showFiles] = useSyncedBooleanQueryParam({
    param: 'showFiles',
    userProperty: UserProperties.SHOW_ALL_CHANGES_FILES,
  })

  const changesListContextValue = useMemo(
    () => ({...DEFAULT_CHANGES_LIST_CONTEXT, showFiles}),
    [showFiles],
  )
  const {withGraph} = changesListContextValue

  const locator = useChangeLogLocator({buildId, buildTypeId})

  const buildChangesArg = useMemo(() => getBuildChangesArg({buildId}), [buildId])
  const changeLogArg = useMemo(
    () =>
      locator != null
        ? getChangeLogArg({
            locator,
            changesOptions: {withFiles: showFiles, withUpdatePager: true},
            withGraph,
          })
        : null,
    [locator, showFiles, withGraph],
  )

  const [fetchBuildChanges, {changesCount, artifactChangesCount, ready}] =
    restApi.endpoints.getBuildNormalized.useLazyQuery({
      selectFromResult: ({data}) => ({
        changesCount: getBuildChangesCount(data),
        artifactChangesCount: getArtifactChangesCount(data),
        ready: data != null,
      }),
    })

  const dependencyChangeLocator = `${baseLocator},count:1,changesFromDependencies:true`
  const {hasDependencyChanges} = restApi.endpoints.getAllChanges.useQuery(
    getChangesArg(dependencyChangeLocator),
    {
      selectFromResult: ({data}) => ({
        hasDependencyChanges: data?.changes?.result?.length ?? 0,
      }),
    },
  )
  const [fetchChangeLog] = restApi.endpoints.getChangeLog.useLazyQuerySubscription()

  const fetch = useCallback(() => {
    if (changeLogArg != null) {
      fetchBuildChanges(buildChangesArg)
      fetchChangeLog(changeLogArg)
    }
  }, [buildChangesArg, changeLogArg, fetchBuildChanges, fetchChangeLog])

  useEffect(() => {
    if (buildTypeInternalId != null && (queued || running)) {
      return subscribeOnBuildTypeEvents(buildTypeInternalId, [BUILD_CHANGES_LOADED], fetch)
    } else {
      fetch()
      return undefined
    }
  }, [buildTypeInternalId, fetch, queued, running])

  const {onReady} = useContext(SequenceLoaderContext)
  useEffect(() => {
    if (onReady != null && ready && changesCount === 0 && artifactChangesCount === 0) {
      onReady()
    }
  }, [artifactChangesCount, changesCount, ready, onReady])

  const count = changesCount + artifactChangesCount
  const changesType =
    filterOptions.changesType ??
    (changesCount > 0 || hasDependencyChanges ? ChangesType.CHANGES : ChangesType.ARTIFACTS)

  const onSelectChangesType = useCallback(() => {
    if (changesType !== ChangesType.CHANGES) {
      onChangeFilter({changesType: ChangesType.CHANGES}, true)
    }
  }, [onChangeFilter, changesType])
  const onSelectArtifactsChangesType = useCallback(() => {
    if (changesType !== ChangesType.ARTIFACTS) {
      onChangeFilter({changesType: ChangesType.ARTIFACTS}, true)
    }
  }, [onChangeFilter, changesType])

  const title = useChangesCountHeading(changesCount, artifactChangesCount)

  if (locator == null) {
    return null
  }

  return count > 0 || hasDependencyChanges ? (
    <div data-test-changes-advanced-view>
      {changesCount === 0 ||
        (artifactChangesCount === 0 && <H2 className={styles.heading}>{title}</H2>)}
      <div className={styles.filter}>
        {changesCount > 0 && artifactChangesCount > 0 && (
          <ButtonGroup className={styles.changesTypeFilter}>
            <Button onClick={onSelectChangesType} active={changesType === ChangesType.CHANGES}>
              <>
                {'VCS changes'}
                <span className={styles.count}>{changesCount}</span>
              </>
            </Button>
            <Button
              onClick={onSelectArtifactsChangesType}
              active={changesType === ChangesType.ARTIFACTS}
            >
              <>
                {'Artifact dependency changes'}
                <span className={styles.count}>{artifactChangesCount}</span>
              </>
            </Button>
          </ButtonGroup>
        )}
        {changesType === ChangesType.CHANGES && (
          <Suspense>
            <ChangesFilter
              className={styles.changesFilter}
              baseLocator={baseLocator}
              buildTypeId={buildTypeId}
              withIncludeChangesInSettingsToggle={false}
              withChangesFromDependenciesToggle
              commiterSelectQueryRef={commiterSelectQueryRef}
            />
          </Suspense>
        )}
      </div>
      {changesType === ChangesType.CHANGES && (
        <ChangeContext.Provider value={changesListContextValue}>
          <Suspense>
            <ChangesLog buildTypeId={buildTypeId} locator={locator} />
          </Suspense>
        </ChangeContext.Provider>
      )}
      {changesType === ChangesType.ARTIFACTS && buildId != null && (
        <ArtifactChanges
          buildId={buildId}
          visibleCount={VISIBLE_COUNT}
          withAdvancedRow
          withExpandableLines
        />
      )}
    </div>
  ) : (
    <div data-test-changes-advanced-view className={styles.emptyList}>
      {`There are no changes found${queued ? ' or they are not yet collected' : ''}`}
    </div>
  )
}

export default memo(ChangesAdvancedView)
